/**
 * CardScanner Component
 * Main container component for the card scanning flow
 */
import { useState, useEffect, useCallback, Suspense } from 'react';
import { ScanView } from './ScanView';
import { ProcessingView } from './ProcessingView';
import { ResultView } from './ResultView';
import { useScanState } from '@/hooks/ScanStateContext';
import { scannerApiService } from '@/services/scannerApiService';
import { getCardById } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { PokemonCard } from '@/lib/api';
import { addCardToCollection } from '@/lib/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cameraService } from '@/services/cameraService';
import { useIsMobile } from '@/hooks/use-mobile';
import { scanQuotaService } from '@/services/scanQuotaService';
import { CardSuccessCelebration } from './CardSuccessCelebration';
import { useScanQuota } from '@/hooks/useScanQuota';

export function CardScanner() {
  const {
    state,
    startScanning,
    setImageCapture,
    setProcessingProgress,
    setScanSuccess,
    setScanError,
    resetScan
  } = useScanState();
  
  const [card, setCard] = useState<PokemonCard | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [capturedImageBlob, setCapturedImageBlob] = useState<Blob | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [scanAvailable, setScanAvailable] = useState<boolean | null>(null);
  const isMobile = useIsMobile();
  const { refreshQuota } = useScanQuota();
  
  // Check if user has scans available before starting
  useEffect(() => {
    const checkScanAvailability = async () => {
      try {
        const hasScans = await scanQuotaService.hasAvailableScans();
        setScanAvailable(hasScans);
        
        if (!hasScans) {
          toast({
            title: "No scans remaining",
            description: "Subscribe to get more scans or upgrade to Pro for unlimited scanning.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error("Error checking scan availability:", error);
        setScanAvailable(false);
      }
    };
    
    checkScanAvailability();
  }, []);
  
  // Initialize components and dependencies
  useEffect(() => {
    let mounted = true;
    const timer = setTimeout(() => {
      if (mounted) {
        setInitializing(false);
      }
    }, 1000); // Short delay to allow for smoother animation
    
    return () => {
      mounted = false;
      clearTimeout(timer);
      resetScan();
      
      // Ensure camera is stopped when component unmounts
      if (cameraService.isActive()) {
        cameraService.stopCamera();
      }
    };
  }, [resetScan]);

  // Process captured image with API
  const processImage = useCallback(async (imageBlob: Blob) => {
    // Create an object URL from the Blob for preview
    const imageUrl = URL.createObjectURL(imageBlob);
    
    // Update state with image and move to processing state
    setImageCapture(imageUrl);
    setCapturedImageBlob(imageBlob);
    setIsRetrying(false);
    
    try {
      // Submit image to API service (backend will handle quota decrement)
      const result = await scannerApiService.identifyCard(
        imageBlob, 
        (progress) => {
          setProcessingProgress(progress);
        }
      );
      
      if (!result.success || !result.cardId) {
        setScanError(result.error || 'Failed to identify card');
        return;
      }
      
      // Get full card details
      const cardDetails = await getCardById(result.cardId);
      
      if (!cardDetails) {
        setScanError('Failed to fetch card details');
        return;
      }
      
      // Update state with successful results
      setCard(cardDetails);
      setScanSuccess(cardDetails);
      
      // Invalidate quota cache after successful scan
      scanQuotaService.invalidateCache();
      refreshQuota();
      
      // Reset retry count on success
      setRetryCount(0);
    } catch (error: any) {
      console.error('Error in card scan flow:', error);
      setScanError(error.message || 'An unexpected error occurred');
    } finally {
      // Clean up the URL object to prevent memory leaks
      URL.revokeObjectURL(imageUrl);
    }
  }, [setImageCapture, setProcessingProgress, setScanError, setScanSuccess, refreshQuota]);

  // Handle image submission to API
  const handleImageCaptured = useCallback(async (imageBlob: Blob) => {
    // Check scan availability again right before processing
    try {
      scanQuotaService.invalidateCache();
      refreshQuota();
      const hasScans = await scanQuotaService.hasAvailableScans();
      
      if (!hasScans) {
        toast({
          title: "No scans remaining",
          description: "Subscribe to get more scans or upgrade to Pro for unlimited scanning.",
          variant: "destructive"
        });
        setScanError("No scans remaining");
        return;
      }
      
      await processImage(imageBlob);
    } catch (error) {
      console.error("Error checking scan availability:", error);
      setScanError("Failed to verify scan availability");
    }
  }, [processImage, setScanError, refreshQuota]);

  // Start scanning process
  const handleStartScan = useCallback(() => {
    if (scanAvailable === false) {
      toast({
        title: "No scans remaining",
        description: "Subscribe to get more scans or upgrade to Pro for unlimited scanning.",
        variant: "destructive"
      });
      return;
    }
    startScanning();
  }, [startScanning, scanAvailable]);
  
  // Handle a retry from error or new scan
  const handleRetry = useCallback(() => {
    resetScan();
    startScanning();
    setRetryCount(0);
  }, [resetScan, startScanning]);
  
  // Handle retry from processing view
  const handleProcessingRetry = useCallback(() => {
    // Increment retry count to track multiple retries
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setIsRetrying(true);
    
    // If too many retries (3+), suggest starting over with a new image
    if (newRetryCount >= 3) {
      toast({
        title: "Multiple retries detected",
        description: "Taking a new picture might work better. Try repositioning your card with better lighting.",
        duration: 5000,
      });
      // After 3 retries, start over with camera
      handleRetry();
      return;
    }
    
    // Otherwise retry with the same image
    if (capturedImageBlob) {
      // Show toast for retries
      toast({
        title: "Retrying...",
        description: "Sending image to server again",
      });
      // Retry processing the same image
      processImage(capturedImageBlob);
    } else {
      // Start over with the camera if image is not available anymore
      handleRetry();
    }
  }, [capturedImageBlob, processImage, handleRetry, retryCount]);
  
  // Handle save to collection with enhanced feedback
  const handleSaveToCollection = useCallback(async () => {
    if (!card) return;
    
    try {
      const success = await addCardToCollection(card.id, card);
      if (success) {
        // Show celebration animation with enhanced details
        setShowCelebration(true);
      }
    } catch (error) {
      console.error('Error saving card to collection:', error);
      toast({
        title: 'Error',
        description: 'Failed to save card to your collection. Please try again.',
        variant: 'destructive',
      });
    }
  }, [card]);

  // Handle celebration completion
  const handleCelebrationComplete = useCallback(() => {
    setShowCelebration(false);
  }, []);

  // Render different views based on scan state
  return (
    <div className="w-full relative">
      <AnimatePresence mode="wait">
        {initializing ? (
          // Initial loading state before any scanning
          <motion.div 
            key="initializing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`w-full max-w-xl mx-auto ${isMobile ? 'aspect-[3/4]' : 'aspect-[4/3]'} bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center`}
          >
            <div className="text-center">
              <div className="flex justify-center">
                <Loader className="w-10 h-10 text-pokemon-blue animate-spin" />
              </div>
              <p className="mt-4 text-lg text-gray-700 font-medium">Preparing scanner...</p>
            </div>
          </motion.div>
        ) : state.status === 'idle' ? (
          // Start scanning button with improved design
          <motion.div 
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center p-4"
          >
            <motion.div 
              className={`w-full max-w-xl mx-auto bg-gradient-to-b from-blue-50 to-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-lg flex flex-col items-center justify-center py-10 px-6 ${isMobile ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="w-24 h-24 mb-6 bg-pokemon-blue/10 rounded-full flex items-center justify-center"
              >
                <div className="w-16 h-16 bg-pokemon-blue/20 rounded-full flex items-center justify-center">
                  <div className="w-12 h-12 bg-pokemon-blue rounded-full flex items-center justify-center text-white">
                    <Camera className="w-6 h-6" />
                  </div>
                </div>
              </motion.div>
              
              <motion.h3 
                className="text-2xl font-bold mb-3 text-gray-800"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Ready to Scan
              </motion.h3>
              
              <motion.p 
                className="text-gray-600 mb-8 text-center max-w-md leading-relaxed"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                This will open your camera. Position a Pokémon card in the frame and tap the Capture button to identify it.
              </motion.p>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button 
                  onClick={handleStartScan}
                  className="bg-pokemon-blue hover:bg-pokemon-blue/90 text-white px-8 py-3 rounded-lg text-lg font-medium shadow-lg"
                  size="lg"
                  disabled={scanAvailable === false}
                >
                  <Camera className="mr-2 h-5 w-5" />
                  Start Scanning
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        ) : (
          // Active views based on current state
          <motion.div 
            key={state.status}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {(state.status === 'initializing' || state.status === 'searching' || state.status === 'detecting') && (
              <ScanView 
                onImageCaptured={handleImageCaptured}
              />
            )}
            
            {(state.status === 'processing' || state.status === 'capturing') && (
              <ProcessingView 
                message={state.message}
                progress={state.progress || 0}
                imageUrl={state.imageUrl}
                error={state.errorMessage}
                onRetry={handleProcessingRetry}
              />
            )}
            
            {state.status === 'success' && state.cardData && (
              <ResultView 
                card={state.cardData}
                onScanAgain={handleRetry}
                onSaveToCollection={handleSaveToCollection}
              />
            )}
            
            {state.status === 'error' && (
              <motion.div 
                className="text-center p-6 space-y-4 max-w-xl mx-auto"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, type: "spring" }}
              >
                <div className={`w-full ${isMobile ? 'aspect-[3/4]' : 'aspect-[4/3]'} bg-red-50 rounded-lg flex flex-col items-center justify-center p-6`}>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-red-600 mb-2">Scan Failed</h2>
                  <p className="text-gray-700 mb-6">{state.errorMessage || 'An error occurred'}</p>
                  <motion.button 
                    onClick={handleRetry}
                    className="px-4 py-2 bg-pokemon-blue text-white rounded-lg hover:bg-pokemon-dark-blue transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Try Again
                  </motion.button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Success celebration overlay with enhanced props */}
      {showCelebration && card && (
        <CardSuccessCelebration
          onClose={handleCelebrationComplete}
          cardName={card.name}
          cardValue={card.cardmarket?.prices?.averageSellPrice || card.tcgplayer?.prices?.[Object.keys(card.tcgplayer?.prices || {})[0]]?.market || 0}
          cardImage={card.images.small}
        />
      )}
    </div>
  );
}
